import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SprintProductEntity, useProduct, useSprint, useSprintProductReports } from '@data';
import { getBriefReports } from '@data/api/utils/helpers';

import { usePricedSprint } from '../../clientReporting/sprintProduct/PricedSprintTotals';
import { productStoreQuery, useProductStore } from '../../products/productDetail/useProductStore';
import { pricingGaps, type PricingGaps } from './pricingGaps';

/** How long the mail waits for the product's figures before it goes out without a cost row. */
const PRICE_WAIT_MS = 6000;

type SprintReportCost = {
  settled: boolean;
  totalCost?: number;
  gaps: PricingGaps;
  /** The sprint's first day, which decides the rate version it is priced under. */
  sprintStart?: string;
  /** Reads the figures again after they were edited in this window, whose store is already current. */
  recheck: () => void;
};

const NO_GAPS: PricingGaps = { unlevelled: [], unrated: [], uncosted: [], missingRates: [], chapters: [], workers: [] };

/** The sprint's cost as the report page prices it, and who worked it and cannot be priced. */
export const useSprintReportCost = (sprintProduct?: SprintProductEntity): SprintReportCost => {
  const productId = sprintProduct?.product?.id ?? sprintProduct?.productId ?? '';
  const { data: product, refetch: refetchProduct } = useProduct(productId || undefined);
  const { data: sprint } = useSprint(sprintProduct?.sprint?.id ?? sprintProduct?.sprintId);
  const { data: reports } = useSprintProductReports({ sprint, productId });
  // Read-only: opening a product with no version for this year would otherwise write one.
  const store = useProductStore(productId, true);
  const queryClient = useQueryClient();
  // Moves when a recheck reloaded the store: it is read synchronously, so nothing else would redraw.
  const [rechecked, setRechecked] = useState(0);

  const groups = useMemo(() => (reports?.items ? getBriefReports(reports.items) : []), [reports]);
  const { cost, priced, team, sprintsReady } = usePricedSprint(
    product,
    sprint?.id ?? '',
    sprint?.startDate?.slice(0, 10) ?? '',
    groups,
    // Read again once the store has loaded: before that every read is empty, and nothing else would redraw it.
    store.isReady ? 1 + store.revision + rechecked : 0,
  );

  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setGaveUp(true), PRICE_WAIT_MS);
    return () => clearTimeout(timer);
  }, []);

  // A fixed price is placed by the sprint list, so a cost settled before it arrives would latch €0.
  const ready = store.isReady && sprintsReady && !!product && !!sprint && !!reports;
  const gaps = useMemo(
    () => (product && reports ? pricingGaps(groups, team, product) : NO_GAPS),
    [groups, team, product, reports],
  );
  const hasGaps = gaps.unlevelled.length + gaps.unrated.length + gaps.uncosted.length > 0;

  // Both are filled in on the product's Team tab and the app does not refetch on focus, so coming back rechecks them.
  useEffect(() => {
    if (!hasGaps) return undefined;
    const recheck = (): void => {
      Promise.all([
        refetchProduct(),
        queryClient.refetchQueries({ queryKey: productStoreQuery(productId, true).queryKey }),
      ]).then(() => setRechecked(count => count + 1));
    };
    window.addEventListener('focus', recheck);
    return () => window.removeEventListener('focus', recheck);
  }, [hasGaps, refetchProduct, queryClient, productId]);

  const recheck = (): void => {
    refetchProduct().then(() => setRechecked(count => count + 1));
  };
  const sprintStart = sprint?.startDate?.slice(0, 10);

  // Kept once settled with everyone priced: a price that moved after that would reload the draft under the edits.
  const settledAs = useRef<{ settled: true; totalCost?: number }>();
  const current = { settled: true as const, totalCost: priced ? Math.round(cost * 100) / 100 : undefined };
  if (!settledAs.current) {
    if (ready && !hasGaps) settledAs.current = current;
    else if (!ready && (store.isError || gaveUp)) settledAs.current = { settled: true };
  }
  const extras = { gaps, sprintStart, recheck };
  if (settledAs.current) return { ...settledAs.current, ...extras };
  // No cost while anyone is unpriced, and not kept, so filling them in reprices the mail.
  return ready ? { settled: true, ...extras } : { settled: false, ...extras };
};
